/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";

// Sub item
const SliderItem = React.forwardRef(({ item, index, onClick }, ref) => {
  return (
    <div
      ref={ref}
      className="absolute top-1/2 left-1/2 cursor-pointer select-none rounded-2xl shadow-2xl bg-transparent transform-origin-[0%_100%] pointer-events-auto w-[var(--width)] h-[var(--height)] -mt-[calc(var(--height)/2)] -ml-[calc(var(--width)/2)] overflow-hidden will-change-transform"
      style={{
        "--width": "clamp(150px, 32vw, 360px)",
        "--height": "clamp(200px, 40vw, 420px)",
        transition: "none",
        display: "block",
      }}
      onClick={onClick}
    >
      <div className="slider-item-content absolute inset-0 z-10 transition-opacity duration-300 ease-out will-change-opacity" style={{ opacity: 1 }}>
        <div className="absolute z-10 text-white bottom-5 left-5 text-[clamp(20px,3vw,30px)] drop-shadow-md">
          {item.title}
        </div>
        <div className="absolute z-10 text-white top-2.5 left-5 text-[clamp(20px,10vw,80px)]">{item.num}</div>
        <div className="w-full h-full flex items-center justify-center bg-transparent">
          <img
            src={item.imageUrl}
            alt={item.title}
            className="max-w-full max-h-full w-auto h-auto object-contain pointer-events-none"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </div>
  );
});
SliderItem.displayName = "SliderItem";

const ThreeDSlider = ({ items = [], speedWheel = 0.05, speedDrag = -0.15, containerStyle = {}, onItemClick }) => {
  const progressRef = useRef(50);
  const targetProgressRef = useRef(50);
  const isDownRef = useRef(false);
  const startXRef = useRef(0);
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const itemRefs = useRef([]);
  const cacheRef = useRef({});
  const numItems = items.length || 1;

  const getClientX = (e) => ("touches" in e ? e.touches[0].clientX : e.clientX);

  const update = useCallback(() => {
    if (!itemRefs.current.length) return;
    progressRef.current += (targetProgressRef.current - progressRef.current) * 0.1;
    const activeFloat = Math.max(0, Math.min(progressRef.current, 100)) / 100 * (numItems - 1);

    itemRefs.current.forEach((el, index) => {
      if (!el) return;
      const denom = numItems > 1 ? numItems - 1 : 1;
      const ratio = (index - activeFloat) / denom;
      // tighter spacing between slides so edges almost touch on all screen sizes
      const tx = ratio * 420;
      const ty = ratio * 140;
      const rot = ratio * 120;
      const dist = Math.abs(index - activeFloat);
      const z = numItems - dist;
      const opacity = Math.max(0, Math.min(1, (z / numItems) * 3 - 2));

      const newTransform = `translate3d(${tx}%, ${ty}%, 0) rotate(${rot}deg)`;
      const newZIndex = Math.round(z * 10).toString();
      const newOpacity = opacity.toString();

      if (!cacheRef.current[index]) cacheRef.current[index] = { transform: "", zIndex: "", opacity: "" };
      const cache = cacheRef.current[index];
      if (cache.transform !== newTransform) {
        el.style.transform = newTransform;
        cache.transform = newTransform;
      }
      if (cache.zIndex !== newZIndex) {
        el.style.zIndex = newZIndex;
        cache.zIndex = newZIndex;
      }
      const inner = el.querySelector(".slider-item-content");
      if (inner && cache.opacity !== newOpacity) {
        inner.style.opacity = newOpacity;
        cache.opacity = newOpacity;
      }
    });
  }, [numItems]);

  useEffect(() => {
    let active = true;
    const loop = () => {
      if (!active) return;
      update();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      active = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [update]);

  // Auto-play: advance progress smoothly كل 2.2 ثانية
  useEffect(() => {
    if (!items.length) return;
    const id = setInterval(() => {
      const current = targetProgressRef.current;
      const step = 12; // percentage
      const next = current + step;
      targetProgressRef.current = next > 100 ? 0 : next;
    }, 2200);
    return () => clearInterval(id);
  }, [items.length]);

  const handleWheel = useCallback(
    (e) => {
      const next = Math.max(0, Math.min(100, targetProgressRef.current + e.deltaY * speedWheel));
      targetProgressRef.current = next;
      e.preventDefault();
    },
    [speedWheel]
  );

  const handleMouseDown = useCallback((e) => {
    isDownRef.current = true;
    startXRef.current = getClientX(e) ?? 0;
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDownRef.current) return;
      const x = getClientX(e);
      if (x == null) return;
      const diff = (x - startXRef.current) * speedDrag;
      targetProgressRef.current = Math.max(0, Math.min(100, targetProgressRef.current + diff));
      startXRef.current = x;
    },
    [speedDrag]
  );

  const handleMouseUp = useCallback(() => {
    isDownRef.current = false;
  }, []);

  const handleClick = useCallback(
    (item, index) => {
      const denom = numItems > 1 ? numItems - 1 : 1;
      targetProgressRef.current = (index / denom) * 100;
      onItemClick?.(item, index);
    },
    [numItems, onItemClick]
  );

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const wheelOpts = { passive: false };
    c.addEventListener("wheel", handleWheel, wheelOpts);
    c.addEventListener("mousedown", handleMouseDown);
    c.addEventListener("touchstart", handleMouseDown, { passive: true });
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleMouseMove, { passive: true });
    document.addEventListener("touchend", handleMouseUp);
    return () => {
      c.removeEventListener("wheel", handleWheel);
      c.removeEventListener("mousedown", handleMouseDown);
      c.removeEventListener("touchstart", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleMouseMove);
      document.removeEventListener("touchend", handleMouseUp);
    };
  }, [handleWheel, handleMouseDown, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden bg-transparent"
      style={{ height: "min(70vh, 440px)", ...containerStyle }}
    >
      <div className="relative z-10 h-full overflow-hidden pointer-events-none scale-[0.78] sm:scale-90 md:scale-95 w-full flex items-center justify-center">
        {items.map((item, index) => (
          <SliderItem key={`slider-item-${index}`} ref={(el) => (itemRefs.current[index] = el)} item={item} index={index} onClick={() => handleClick(item, index)} />
        ))}
      </div>
    </div>
  );
};

export default ThreeDSlider;
